import { CognitoUserPool, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';
import { loadAwsConfig } from '../aws-exports';

// Forgot password: initiate reset
export async function forgotPassword(username) {
  const pool = await getUserPool();
  const user = new CognitoUser({ Username: username, Pool: pool });
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: (data) => resolve(data),
      onFailure: (err) => reject(err),
      inputVerificationCode: (data) => resolve({ inputVerificationCode: true, user, data }),
    });
  });
}

// Reset password: confirm new password with code
export async function resetPassword(username, code, newPassword) {
  const pool = await getUserPool();
  const user = new CognitoUser({ Username: username, Pool: pool });
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(true),
      onFailure: (err) => reject(err),
    });
  });
}


let userPool = null;

async function getUserPool() {
  if (userPool) return userPool;
  const config = await loadAwsConfig();
  const { userPoolId, userPoolClientId } = config.Auth.Cognito;
  userPool = new CognitoUserPool({ UserPoolId: userPoolId, ClientId: userPoolClientId });
  return userPool;
}

export const ROLES = { USER: 'user', ADMIN: 'admin' };
const ROLE_HIERARCHY = { [ROLES.USER]: 0, [ROLES.ADMIN]: 2 };

export function hasMinRole(userRole, requiredRole) {
  return (ROLE_HIERARCHY[userRole] ?? -1) >= (ROLE_HIERARCHY[requiredRole] ?? 99);
}

export async function signIn(username, password) {
  const pool = await getUserPool();
  const user = new CognitoUser({ Username: username, Pool: pool });
  const authDetails = new AuthenticationDetails({ Username: username, Password: password });

  return new Promise((resolve, reject) => {
    user.authenticateUser(authDetails, {
      onSuccess: (session) => resolve(session),
      onFailure: (err) => reject(err),
      newPasswordRequired: (userAttributes) => {
        delete userAttributes.email_verified;
        delete userAttributes.phone_number_verified;
        resolve({ newPasswordRequired: true, user, userAttributes });
      },
    });
  });
}

export function completeNewPassword(cognitoUser, newPassword, userAttributes) {
  return new Promise((resolve, reject) => {
    cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
      onSuccess: (session) => resolve(session),
      onFailure: (err) => reject(err),
    });
  });
}

export async function signUp(username, password, email) {
  const pool = await getUserPool();
  const attributeList = [
    { Name: 'email', Value: email },
  ].map((attr) => {
    const { CognitoUserAttribute } = require('amazon-cognito-identity-js');
    return new CognitoUserAttribute(attr);
  });

  return new Promise((resolve, reject) => {
    pool.signUp(username, password, attributeList, null, (err, result) => {
      if (err) return reject(err);
      resolve(result);
    });
  });
}

export async function confirmSignUp(username, code) {
  const pool = await getUserPool();
  const user = new CognitoUser({ Username: username, Pool: pool });
  return new Promise((resolve, reject) => {
    user.confirmRegistration(code, true, (err, result) => {
      if (err) return reject(err);
      resolve(result);
    });
  });
}

export async function resendConfirmationCode(username) {
  const pool = await getUserPool();
  const user = new CognitoUser({ Username: username, Pool: pool });
  return new Promise((resolve, reject) => {
    user.resendConfirmationCode((err, result) => {
      if (err) return reject(err);
      resolve(result);
    });
  });
}

export async function signOut() {
  const pool = await getUserPool();
  const user = pool.getCurrentUser();
  if (user) user.signOut();
}

export async function getCurrentSession() {
  const pool = await getUserPool();
  const user = pool.getCurrentUser();
  if (!user) throw new Error('No current user');
  return new Promise((resolve, reject) => {
    user.getSession((err, session) => (err ? reject(err) : resolve(session)));
  });
}

export function getUserRole(session) {
  const payload = session.getIdToken().decodePayload();
  const groups = payload['cognito:groups'] || [];
  if (groups.includes('Admins')) return ROLES.ADMIN;
  return ROLES.USER;
}

export async function getUserAttributes() {
  const pool = await getUserPool();
  const user = pool.getCurrentUser();
  if (!user) throw new Error('No current user');
  return new Promise((resolve, reject) => {
    user.getSession((err) => {
      if (err) return reject(err);
      user.getUserAttributes((attrErr, attributes) => {
        if (attrErr) return reject(attrErr);
        const attrs = {};
        attributes.forEach((a) => { attrs[a.getName()] = a.getValue(); });
        resolve(attrs);
      });
    });
  });
}
